import { barColors } from 'features/Engagement/engagementConstants'
import Gauge from 'features/Engagement/Gauge'
import moment from 'moment'
import { useEffect, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import engagementApi from 'services/api/engagement'

import styles from './ConversationsGraph.module.scss'
import ConversionGraphTooltip from './Tooltip'

const ConversationsGraph = ({ startDate, endDate, stats, statsStatus, hotelIds }) => {
  const [barData, setBarData] = useState([])
  const [barDataStatus, setBarDataStatus] = useState('pending')
  const [barDataError, setBarDataError] = useState()

  const barWidth = Math.min(15, (12 / barData.length) * 12)

  useEffect(() => {
    setBarDataStatus('pending')

    const abortController = new AbortController()

    engagementApi
      .consumerDetails(
        {
          subsidiaryIds: hotelIds,
          toDate: endDate.format('YYYY-MM-DD'),
          fromDate: startDate.format('YYYY-MM-DD')
        },
        abortController
      )
      .then(data => {
        setBarData(
          Object.keys(data).map(k => ({
            name: moment(k).format('MMM D'),
            'on time': data[k].totalReplyBackMessagesOnSameDay,
            delayed: data[k].totalReplyBackMessagesOnDelayed,
            total: data[k].totalReplyBackMessages
          }))
        )
        setBarDataStatus('fetched')
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setBarDataStatus('rejected')
          setBarDataError(err)
        }
      })

    return () => abortController.abort()
  }, [startDate, endDate, hotelIds])

  if (barDataStatus === 'pending') {
    return <div className={styles.wrapper}></div>
  }

  if (barDataStatus === 'rejected') {
    return (
      <div className={styles.wrapper}>Failed to get data: {barDataError?.toString()}</div>
    )
  }

  return (
    <div className={styles.wrapper}>
      {statsStatus === 'fetched' && (
        <>
          {' '}
          <div className={styles.conversationsGauge}>
            {statsStatus === 'fetched' && (
              <>
                {' '}
                <h3 className={styles.title}>Replied conversations</h3>
                <div className={styles.gaugeHolder}>
                  <Gauge
                    percentage={
                      stats.totalRepliedBackChats / stats.totalConsumerRespondedChats
                    }
                  />
                </div>
                <p className={styles.gaugeStats}>
                  {stats.totalRepliedBackChats} of {stats.totalConsumerRespondedChats}{' '}
                  conversations
                </p>
              </>
            )}
          </div>
          <div className={styles.graphHolder}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                data={barData}
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 15
                }}
              >
                <Legend
                  wrapperStyle={{ marginRight: 14, marginTop: 24 }}
                  className={styles.legend}
                  formatter={value => {
                    return {
                      'on time': 'replies sent on-time',
                      delayed: 'replies delayed'
                    }[value]
                  }}
                  verticalAlign="top"
                  align="right"
                />
                <CartesianGrid stroke="#F2F2F2" vertical={false} />
                <XAxis
                  dataKey="name"
                  axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                  tick={{
                    fill: '#7D7D7D',
                    fontSize: 12,
                    transform: 'translate(0, 14)'
                  }}
                  tickSize={0}
                  padding={{ top: 30, bottom: 20, right: 40 }}
                />
                <YAxis
                  tickSize={0}
                  axisLine={false}
                  allowDecimals={false}
                  tick={{
                    fill: '#7D7D7D',
                    fontSize: 12,
                    transform: 'translate(-10, 0)'
                  }}
                  padding={{ top: 40, bottom: 0 }}
                />
                <Tooltip
                  content={<ConversionGraphTooltip />}
                  labelFormatter={value => `${value} 2`}
                  cursor={{
                    stroke: 'red',
                    strokeWidth: 0,
                    fill: '#dedede',
                    opacity: 0.22
                  }}
                />
                <Bar dataKey="on time" fill={barColors[0]} barSize={barWidth} />
                <Bar dataKey="delayed" fill={barColors[2]} barSize={barWidth} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  )
}

export default ConversationsGraph
